import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Mail, Lock, User, Eye, EyeOff, ShieldCheck, KeyRound } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PeepingLogo } from "@/components/PeepingLogo";

const TOKEN_KEY = "peeping_setup_token";

export function Field({ icon: Icon, label, right, ...props }) {
  return (
    <div>
      <label htmlFor={props.id} className="mb-1 block pl-1 text-xs font-medium text-[#a7a0af]">{label}</label>
      <div className="relative">
        <Icon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a7a0af]" />
        <input autoCapitalize="none" autoCorrect="off" spellCheck={false} className="field w-full rounded-2xl py-4 pl-11 pr-11 text-base sm:text-sm" {...props} />
        {right}
      </div>
    </div>
  );
}

export default function Setup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { startSession } = useAuth();
  const urlToken = params.get("token") || "";
  const [token, setToken] = useState(() => urlToken || window.sessionStorage.getItem(TOKEN_KEY) || "");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (urlToken) window.sessionStorage.setItem(TOKEN_KEY, urlToken);
    api.get("/setup/status").then((r) => setStatus(r.data)).catch(() => setStatus({ initialized: false }));
  }, [urlToken]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    // Read straight from the form so Safari/iPad autofill values are always captured.
    const f = new FormData(e.currentTarget);
    const v = (k) => String(f.get(k) || "");
    const tk = (v("token") || token).trim();
    const username = v("username").trim();
    const email = v("email").trim().toLowerCase();
    const password = v("password");
    if (!tk) return setError("Setup token missing. Open the full setup link, or paste the token above.");
    if (username.length < 3) return setError("Username must be at least 3 characters.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError("Enter a valid email address.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== v("confirm")) return setError("Passwords do not match — please re-type both password fields.");
    setLoading(true);
    try {
      const { data } = await api.post("/setup/init", { token: tk, username, email, password });
      window.sessionStorage.removeItem(TOKEN_KEY);
      startSession(data);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const eye = (
    <button type="button" onClick={() => setShow(!show)} aria-label="Toggle password" data-testid="setup-toggle-password" className="absolute right-4 top-1/2 -translate-y-1/2 text-[#a7a0af]">
      {show ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  );

  return (
    <div data-testid="setup-screen" className="relative min-h-screen bg-[#080808] grain">
      <div className="absolute -top-16 -right-10 h-56 w-56 glow-violet opacity-40" />
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-6 pb-10 pt-12">
        <div className="flex flex-col items-center">
          <PeepingLogo className="w-48" />
          <span className="mt-3 flex items-center gap-1.5 rounded-full border border-[#6a00ff]/40 bg-[#6a00ff]/10 px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-wider text-[#c9a6ff]">
            <ShieldCheck size={12} /> Super Admin Setup
          </span>
        </div>

        {status?.initialized ? (
          <div data-testid="setup-already" className="mt-12 rounded-2xl glass p-6 text-center">
            <h1 className="text-xl font-bold">Setup already completed</h1>
            <p className="mt-2 text-sm text-[#a7a0af]">A super administrator already exists for this environment.</p>
            <Link to="/login" className="mt-6 inline-block text-sm font-semibold text-[#d42be8]">Go to sign in</Link>
          </div>
        ) : (
          <>
            <div className="mt-10">
              <h1 className="text-2xl font-bold">Create your administrator</h1>
              <p className="mt-1 text-sm text-[#a7a0af]">Choose your own email and password. This one-time setup link can be used only once.</p>
            </div>
            <form onSubmit={submit} className="mt-6 flex flex-col gap-3" noValidate>
              {!urlToken && (
                <Field id="setup-token" icon={KeyRound} label="Setup token" name="token" data-testid="setup-token" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste your setup token" autoComplete="off" />
              )}
              <Field id="setup-username" icon={User} label="Username" name="username" data-testid="setup-username" placeholder="Choose a username" autoComplete="nickname" />
              <Field id="setup-email" icon={Mail} label="Email address" name="email" type="email" inputMode="email" data-testid="setup-email" placeholder="you@example.com" autoComplete="email" />
              <Field id="setup-password" icon={Lock} label="Password" name="password" type={show ? "text" : "password"} data-testid="setup-password" placeholder="At least 8 characters" autoComplete="new-password" right={eye} />
              <Field id="setup-confirm" icon={Lock} label="Confirm password" name="confirm" type={show ? "text" : "password"} data-testid="setup-confirm" placeholder="Re-enter your password" autoComplete="new-password" />
              {error && (
                <p data-testid="setup-error" role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>
              )}
              <button type="submit" disabled={loading} data-testid="setup-submit" className="btn-brand mt-2 w-full rounded-2xl py-4 text-base font-semibold text-white">
                {loading ? "Creating administrator…" : "Create administrator & sign in"}
              </button>
            </form>
            <p className="mt-5 text-center text-[0.65rem] text-[#6f6878]">
              Tip for iPad/iPhone: tap the eye icon to check both passwords match. Your password is hashed and never stored in plain text.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
